<script>
export default {
  props: {
    fields: {
      type: Array,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localValues: {},
    };
  },
  watch: {
    fields: {
      handler() {
        this.initializeLocalValues();
      },
      immediate: true,
    },
  },
  methods: {
    initializeLocalValues() {
      this.localValues = {};
      this.fields.forEach(({ attributes }) => {
        this.localValues[attributes.key] = this.getFieldValue(attributes);
      });
    },
    getFieldValue(attributes) {
      switch (attributes.type) {
        case "checkbox":
          return attributes.val_bool;
        case "number":
          return attributes.val_int || attributes.val_float;
        case "multiselect":
        case "multiselect_dropdown":
          return attributes.val_str_list || [];
        default:
          return attributes.val_str || "";
      }
    },
    updateValue(key, value) {
      // Validate the value before updating
      const field = this.fields.find(({ attributes }) => attributes.key === key);
      if (field && this.validateFieldValue(field.attributes, value)) {
        this.localValues[key] = value;
        this.$emit("field-updated", { key, value });
      }
    },

    validateFieldValue(fieldConfig, value) {
      // Basic validation based on field type
      switch (fieldConfig.type) {
        case "number":
          if (value !== "" && value !== null && value !== undefined) {
            const numValue = Number(value);
            if (isNaN(numValue)) {
              this.$bvToast.toast(`${fieldConfig.key} must be a valid number`, {
                title: "Validation Error",
                variant: "danger",
                solid: true,
              });
              return false;
            }
          }
          break;
        case "text":
          // Basic text validation - could add max length, required field validation
          if (typeof value !== "string" && value !== null && value !== undefined) {
            this.$bvToast.toast(`${fieldConfig.key} must be text`, {
              title: "Validation Error",
              variant: "danger",
              solid: true,
            });
            return false;
          }
          break;
        case "ddl":
          // Validate that the selected value is in the options
          if (value && fieldConfig.options) {
            const validOptions = Object.keys(fieldConfig.options);
            if (!validOptions.includes(value)) {
              this.$bvToast.toast(`Invalid selection for ${fieldConfig.key}`, {
                title: "Validation Error",
                variant: "danger",
                solid: true,
              });
              return false;
            }
          }
          break;
        case "multiselect":
        case "multiselect_dropdown":
          // Validate that all selected values are in the options
          if (Array.isArray(value) && fieldConfig.options) {
            const validOptions = Object.keys(fieldConfig.options);
            const invalidSelections = value.filter((v) => !validOptions.includes(v));
            if (invalidSelections.length > 0) {
              this.$bvToast.toast(
                `Invalid selections for ${fieldConfig.key}: ${invalidSelections.join(", ")}`,
                {
                  title: "Validation Error",
                  variant: "danger",
                  solid: true,
                }
              );
              return false;
            }
          }
          break;
      }
      return true;
    },
    getInputComponent(type) {
      switch (type) {
        case "checkbox":
          return "b-form-checkbox";
        case "number":
          return "b-form-input";
        case "ddl":
          return "b-form-select";
        case "multiselect":
        case "multiselect_dropdown":
          return "b-form-select";
        case "text":
        default:
          return "b-form-input";
      }
    },
    getInputProps(attributes) {
      const baseProps = {
        id: attributes.key,
        disabled: !this.editable || !attributes.enabled,
      };

      switch (attributes.type) {
        case "number":
          return {
            ...baseProps,
            type: "number",
            step: attributes.val_float ? 0.01 : 1,
          };
        case "checkbox":
          return {
            ...baseProps,
            switch: true,
          };
        case "ddl":
          return {
            ...baseProps,
            options: this.getSelectOptions(attributes.options),
          };
        case "multiselect":
        case "multiselect_dropdown":
          return {
            ...baseProps,
            multiple: true,
            options: this.getSelectOptions(attributes.options),
          };
        default:
          return {
            ...baseProps,
            type: "text",
            readonly: !this.editable || !attributes.enabled,
          };
      }
    },
    getSelectOptions(options) {
      if (!options || typeof options !== "object") return [];
      return Object.entries(options).map(([value, text]) => ({
        value,
        text: text || value,
      }));
    },
    getFieldStatus(fieldKey) {
      if (
        !this.editable ||
        !this.fields.find(({ attributes }) => attributes.key === fieldKey)?.attributes.enabled
      ) {
        return this.$t(
          "dashboard.donation_form_custom_field_not_enabled",
          "This field is not configured for this campaign and cannot be edited."
        );
      }

      return null;
    },
  },
};
</script>

<template>
  <div>
    <b-row v-for="{ attributes } in fields" :key="attributes.key">
      <b-col cols="12">
        <b-form-group
          :id="`input-group-${attributes.key}`"
          :label="attributes.key"
          :label-for="attributes.key"
          :description="getFieldStatus(attributes.key)"
        >
          <component
            :is="getInputComponent(attributes.type)"
            v-bind="getInputProps(attributes)"
            :value="localValues[attributes.key]"
            @input="updateValue(attributes.key, $event)"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>
